import React, { forwardRef } from 'react';
import { cn } from '@/utils/classNames';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  variant?: 'default' | 'glass' | 'minimal' | 'bordered';
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  label?: string;
  error?: string;
  isGlowing?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      variant = 'default',
      type = 'text',
      leftIcon,
      rightIcon,
      label,
      error,
      isGlowing = false,
      disabled,
      ...props
    },
    ref
  ) => {
    // Base styles for all inputs
    const baseStyles = 'w-full rounded-md px-3 py-2 transition-all duration-200 outline-none';

    // Variant-specific styles
    const variantStyles = {
      default:
        'bg-surface-primary dark:bg-surface-secondary border border-border dark:border-border text-text-primary dark:text-text-primary placeholder:text-text-tertiary',
      glass:
        'glass dark:glass-dark backdrop-blur border border-border/50 text-text-primary dark:text-text-primary placeholder:text-text-tertiary',
      minimal:
        'bg-transparent border-b border-border dark:border-border text-text-primary dark:text-text-primary placeholder:text-text-tertiary',
      bordered:
        'bg-transparent border border-primary text-text-primary dark:text-text-primary placeholder:text-text-tertiary',
    };

    // Focus styles
    const focusStyles = 'focus:ring-2 focus:ring-primary focus:ring-opacity-30 focus:border-primary';

    // State styles for various states
    const disabledStyles = disabled ? 'opacity-50 cursor-not-allowed' : '';
    const errorStyles = error ? 'border-error focus:ring-error focus:ring-opacity-30 focus:border-error' : '';

    // Glow effect
    const glowStyles = isGlowing ? 'border-glow' : '';

    // Padding adjustments when icons are present
    const leftPadding = leftIcon ? 'pl-10' : '';
    const rightPadding = rightIcon ? 'pr-10' : '';

    return (
      <div className="space-y-1">
        {label && (
          <label className="block text-sm font-medium text-text-secondary dark:text-text-secondary">
            {label}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-text-tertiary">
              {leftIcon}
            </div>
          )}

          <input
            type={type}
            ref={ref}
            disabled={disabled}
            className={cn(
              baseStyles,
              variantStyles[variant],
              focusStyles,
              disabledStyles,
              errorStyles,
              glowStyles,
              leftPadding,
              rightPadding,
              className
            )}
            {...props}
          />

          {rightIcon && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-text-tertiary">
              {rightIcon}
            </div>
          )}
        </div>

        {error && <p className="text-xs text-error mt-1">{error}</p>}
      </div>
    );
  }
);

Input.displayName = 'Input';
